import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../../../redux/store";

const API_BASE = process.env.REACT_APP_API_URL || "";

type ReportSummaryState = {
  totalUsersCoinBalance: number | null;
  totalUsersPointBalance: number | null;
  seagmBalance: number | null;
  dataupBalance: number | null;
  totalUsers: number | null;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
};

type ReportSummaryPayload = {
  totalUsersCoinBalance: number | null;
  totalUsersPointBalance: number | null;
  seagmBalance: number | null;
  dataupBalance: number | null;
  totalUsers: number | null;
};

const initialState: ReportSummaryState = {
  totalUsersCoinBalance: null,
  totalUsersPointBalance: null,
  seagmBalance: null,
  dataupBalance: null,
  totalUsers: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

const extractNumeric = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const resolveNumeric = (
  payload: unknown,
  fallbackText: string,
  candidateKeys: string[],
): number | null => {
  const direct = extractNumeric(payload);
  if (direct !== null) return direct;

  if (payload && typeof payload === "object") {
    for (const key of candidateKeys) {
      const candidate = (payload as Record<string, unknown>)[key];
      const resolved = extractNumeric(candidate);
      if (resolved !== null) return resolved;
    }

    const dataField = (payload as Record<string, unknown>).data;
    if (dataField && typeof dataField === "object") {
      return resolveNumeric(dataField, fallbackText, candidateKeys);
    }
  }

  return extractNumeric(fallbackText);
};

const parseResponse = async (response: Response) => {
  const textPayload = await response.text();
  let parsedPayload: unknown = textPayload;
  try {
    parsedPayload = textPayload ? JSON.parse(textPayload) : null;
  } catch {
    parsedPayload = textPayload;
  }
  return { parsedPayload, textPayload };
};

export const fetchReportSummary = createAsyncThunk<
  ReportSummaryPayload,
  void,
  { rejectValue: string; state: RootState }
>("reports/fetchSummary", async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const token = state.auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const response = await fetch(`${API_BASE}/reports/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const { parsedPayload, textPayload } = await parseResponse(response);

    if (!response.ok) {
      throw new Error("Failed to fetch report summary");
    }

    return {
      totalUsersCoinBalance: resolveNumeric(parsedPayload, textPayload, [
        "total_users_coin_balance",
      ]),
      totalUsersPointBalance: resolveNumeric(parsedPayload, textPayload, [
        "total_users_point_balance",
      ]),
      seagmBalance: resolveNumeric(parsedPayload, textPayload, [
        "seagm_balance",
        "balance",
      ]),
      dataupBalance: resolveNumeric(parsedPayload, textPayload, [
        "dataup_balance",
        "wallet_balance",
      ]),
      totalUsers: resolveNumeric(parsedPayload, textPayload, [
        "total_users",
        "users_count",
      ]),
    };
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to fetch report summary");
  }
});

const reportSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReportSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.totalUsersCoinBalance = action.payload.totalUsersCoinBalance;
        state.totalUsersPointBalance = action.payload.totalUsersPointBalance;
        state.seagmBalance = action.payload.seagmBalance;
        state.dataupBalance = action.payload.dataupBalance;
        state.totalUsers = action.payload.totalUsers;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchReportSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch report summary";
      });
  },
});

export const selectReportSummaryLoading = (state: RootState) =>
  state.reports.loading;
export const selectReportSummaryError = (state: RootState) =>
  state.reports.error;
export const selectReportTotalUsersCoinBalance = (state: RootState) =>
  state.reports.totalUsersCoinBalance;
export const selectReportTotalUsersPointBalance = (state: RootState) =>
  state.reports.totalUsersPointBalance;
export const selectReportSeagmBalance = (state: RootState) =>
  state.reports.seagmBalance;
export const selectReportDataupBalance = (state: RootState) =>
  state.reports.dataupBalance;
export const selectReportTotalUsers = (state: RootState) =>
  state.reports.totalUsers;

export default reportSlice.reducer;
